/**
 * WP 3.3, E1: the sheet's showings as chips — `Thu 5 Nov · 20:00` — instead
 * of `ShowingsList`'s prose. One is selected, Calendar writes that one.
 *
 * Pure and controlled: the caller owns which showing is selected and what
 * counts as "now", the same reason `ShowingsList` took `e` and `showings`
 * rather than reading a store. `nextShowing` is exported beside it because
 * both callers that write a commitment from a chip — the sheet's action bar
 * and the overflow menu's "Add to calendar" — need the same default: the
 * next one still to come, not just the first row the query happened to
 * return.
 */
import { useState } from 'react'
import { Button, Chip } from '../ui'
import { fmtDayShort } from '../lib/today'
import { clockIn, dayIn, zoneOfCity } from '../lib/zone'
import type { ShowingLite } from './CardBody'
import { showingVersion, versionTags, versionsDiffer, type Version } from '../lib/filmRank'
import { foldPlaces, groupByPlace, morePlacesText, placesOf, uniqueShowings } from '../lib/places'
import type { FeedRow } from '../lib/types'

export type Showing = ShowingLite

const COLLAPSE_AT = 8

/** A `run` row is an annotation about the whole span, not a date you can
 *  pick — the same line `ShowingsList` drew (Phase 7b's `showing_count`
 *  counts the same way). */
export const occurrencesOf = (showings: Showing[]): Showing[] =>
  showings.filter(s => s.kind !== 'run')

/** The next occurrence at or after `now`, earliest first — the chip that is
 *  selected before anyone taps one, and what "Add to calendar" schedules
 *  when nothing has been tapped. Null where the run has nothing left. */
export function nextShowing(showings: Showing[], now: Date): Showing | null {
  const upcoming = occurrencesOf(showings)
    .filter(s => new Date(s.starts_at).getTime() >= now.getTime())
    .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
  return upcoming[0] ?? null
}

/** `Thu 5 Nov`, or `Thu 5 Nov · 20:00` where the source gave a real time — a
 *  midnight stamp from a dateless extraction is no better than no time at
 *  all (the same rule `ShowingsList`'s own `timeOf` applied). Day and clock
 *  in `tz`, the row's own zone (C2.1: `zoneOfCity(row.city)`). */
export function showingLabel(at: string, tz: string): string {
  const day = fmtDayShort(dayIn(at, tz))
  const time = clockIn(at, tz)
  return time === '00:00' ? day : `${day} · ${time}`
}

export function ShowingChips({ showings, value, onChange, now, tz, mark, version }: {
  showings: Showing[]
  value: Showing | null
  onChange: (s: Showing) => void
  now: Date
  /** The row's zone, which every chip's day and clock are read in (C2.1). */
  tz: string
  /** WP 10.2: a date's ticket status in words — "sold out", "few left" —
   *  after its time, or null for nothing. The caller decides what counts;
   *  this only draws it, in the chip, so it is read with the date. */
  mark?: (s: Showing) => string | null
  /** films-sunday WP B: the version this night prints — `14:00 · OmU` —
   *  or null where it printed none. Part of the date, not a mark after it:
   *  one inline run of text, so the chip's flex gap does not split it. A dub
   *  is drawn quieter than an original; it is a fact worth seeing, and not
   *  the showing an English speaker is looking for. */
  version?: (s: Showing) => Version | null
}) {
  const [expanded, setExpanded] = useState(false)

  // One chip per night: `showing_list` resolves a twin's showings to its
  // canonical, so a night two sources list arrives twice (cards WP4) — and
  // two chips with one key below.
  const sorted = uniqueShowings(occurrencesOf(showings))
    .filter(s => new Date(s.starts_at).getTime() >= now.getTime())
    .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
  if (!sorted.length) return null

  const shown = expanded ? sorted : sorted.slice(0, COLLAPSE_AT)
  const more = sorted.length - shown.length

  return (
    <div className="event-sheet__chips">
      {shown.map(s => {
        const m = mark?.(s) ?? null
        const v = version?.(s) ?? null
        return (
          <Chip
            // WP 10.7 (10.6 finding 1): `starts_at + venue` collides for two
            // showings at the same instant whose venue *string* reads the
            // same — two screens at one multiplex, say — and a duplicate
            // React key is not cosmetic: the loser's behaviour (here, a
            // chip's selected/ticket-tagged state) is unsupported. `venue_id`
            // disambiguates where the string can't; `entry_id` keeps the key
            // unique across sheets sharing one `showings` array reference.
            key={s.entry_id + s.starts_at + (s.venue_id ?? s.venue ?? '')}
            on={value?.starts_at === s.starts_at && value.venue === s.venue}
            onClick={() => onChange(s)}
          >
            {v ? (
              <span>
                {showingLabel(s.starts_at, tz)}
                <span className={v === 'DF' ? 'event-sheet__chip-ver is-dub' : 'event-sheet__chip-ver'}
                      title={v === 'DF' ? 'German dub' : undefined}>
                  {' · '}{v}
                </span>
              </span>
            ) : showingLabel(s.starts_at, tz)}
            {m && <span className="event-sheet__chip-mark">{m}</span>}
          </Chip>
        )
      })}
      {!expanded && more > 0 && (
        <Chip onClick={() => setExpanded(true)}>+{more} more</Chip>
      )}
    </div>
  )
}

/**
 * The sheet's chips as the sheet draws them: one block per building for a
 * set playing several cinemas (cards WP4), in `places` order, with the
 * versions each cinema plays beside its name; one plain row for a one-place
 * row. Every block shares one `value`, so a tap anywhere is the one choice.
 *
 * Lifted out of `EventSheet` so the overflow menu's "Add to calendar" can
 * ask with the same picture (calendar-pick-date): a flat row of chips for a
 * film at ten cinemas would show two `Sat 4 Oct · 11:00` with nothing to
 * tell them apart, and the menu would then be asking a question the sheet
 * answers differently. One drawing, two callers.
 *
 * UI-audit a1-30, Doug chose: with `fold`, the two soonest cinemas and then
 * "9 more cinemas" — VATERLAND's eleven were four screens of chips before
 * the facts. `asking` (the sheet's Calendar is asking which night) opens
 * them all, since a picker that hides nights cannot pick them; the cinema
 * holding the highlighted night is never folded (`foldPlaces`). The menu's
 * own picker passes neither: it is only ever drawn to ask.
 */
export function PlacedChips({ row, occurrences, showings, value, onChange, now, mark, fold = false, asking = false }: {
  row: FeedRow
  /** Future nights, one per night and place — the groups are built from these. */
  occurrences: Showing[]
  /** Everything the row holds, `run` markers included — the one-place row
   *  hands these to `ShowingChips`, which filters as it always did. */
  showings: Showing[]
  value: Showing | null
  onChange: (s: Showing) => void
  now: Date
  mark?: (s: Showing) => string | null
  /** Fold every cinema after the two soonest behind one control. */
  fold?: boolean
  /** The caller is asking for a night: draw every cinema. */
  asking?: boolean
}) {
  // Whether the rest were asked for. The caller keys this component on the
  // row, so the next event opens folded again.
  const [unfolded, setUnfolded] = useState(false)
  const isFilm = row.type === 'screening' || row.discipline === 'film' || row.type === 'release'
  const tz = zoneOfCity(row.city)
  const groups = groupByPlace(occurrences, placesOf(row), row.building ?? row.venue ?? '')
  if (groups.length > 1) {
    const { shown, hidden } = foldPlaces(groups, {
      open: !fold || asking || unfolded,
      holds: g => !!value && g.showings.some(s => s.starts_at === value.starts_at && s.venue === value.venue),
    })
    return (
      <>
        {shown.map(g => {
          const tags = isFilm ? versionTags(g.showings) : []
          return (
            <section key={g.key} className="event-sheet__place" aria-label={g.name}>
              <p className="event-sheet__place-name">
                <b>{g.name}</b>
                {tags.length > 0 && <span className="event-sheet__place-ver">{` · ${tags.join(', ')}`}</span>}
              </p>
              <ShowingChips
                showings={g.showings} value={value} onChange={onChange} now={now} tz={tz} mark={mark}
                version={isFilm && versionsDiffer(g.showings) ? s => showingVersion(s.note) : undefined}
              />
            </section>
          )
        })}
        {hidden > 0 && (
          <div className="event-sheet__moreplaces">
            <Button variant="secondary" size="sm" onClick={() => setUnfolded(true)}>
              {morePlacesText(hidden, isFilm ? 'cinemas' : 'venues')}
            </Button>
          </div>
        )}
      </>
    )
  }
  return (
    <ShowingChips
      showings={showings} value={value} onChange={onChange} now={now} tz={tz} mark={mark}
      version={isFilm && versionsDiffer(showings) ? s => showingVersion(s.note) : undefined}
    />
  )
}
